/**
 * PracticePage — 問題演習 (FR-05/06/07)
 * 5肢択一・即時正誤・解説・出典。focus_mode（ボトムバー非表示）。
 * 経験則 #15: 非同期中はボタン disabled
 * 経験則 #63: 正解/不正解は色+アイコン（animation 非依存）で区別
 */

import { useEffect, useState, useContext, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getNextQuestion, submitAnswer, listCategories } from '../api'
import { ApiClientError } from '../api/client'
import type { QuestionForPractice, AnswerResult, Category, CertificationType } from '../api/types'
import { AuthContext } from '../contexts/AuthContext'
import SourceBlock from '../components/SourceBlock'
import Icon from '../components/Icon'
import Spinner, { PageLoader } from '../components/Spinner'

function ChoiceOption({
  choiceNo,
  choiceText,
  selected,
  onSelect,
  correctChoice,
  userAnswer,
  disabled,
}: {
  choiceNo: number
  choiceText: string
  selected: boolean
  onSelect: () => void
  correctChoice?: number | null
  userAnswer?: number | null
  disabled: boolean
}) {
  const isAnswered = correctChoice != null
  let boxCls = 'border border-border-strong bg-surface'
  let badgeCls = 'bg-surface-alt text-text-secondary'
  let tag: React.ReactNode = null

  if (isAnswered) {
    if (choiceNo === correctChoice) {
      boxCls = 'border-2 border-correct bg-correct-bg'
      tag = (
        <span className="flex items-center gap-1 text-[13px] font-bold text-correct">
          <Icon name="check" size={14} aria-hidden={false} /> 正解
        </span>
      )
    } else if (choiceNo === userAnswer && choiceNo !== correctChoice) {
      boxCls = 'border-2 border-incorrect bg-incorrect-bg'
      tag = (
        <span className="flex items-center gap-1 text-[13px] font-bold text-incorrect">
          <Icon name="x" size={14} aria-hidden={false} /> あなたの解答
        </span>
      )
    }
  } else if (selected) {
    boxCls = 'border-2 border-accent bg-accent-subtle'
    badgeCls = 'bg-accent text-white'
  }

  return (
    <button
      data-testid="choice-option"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected && !isAnswered}
      className={`flex min-h-[52px] w-full items-center gap-3 rounded-md px-3.5 py-3 text-left text-[15px] text-text-primary transition-colors duration-fast ${boxCls} ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
    >
      <span className={`flex h-6 w-6 flex-none items-center justify-center rounded text-[13px] font-bold ${badgeCls}`}>
        {choiceNo}
      </span>
      <span className="flex-1">{choiceText}</span>
      {tag}
    </button>
  )
}

export default function PracticePage() {
  const { certType } = useContext(AuthContext)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const categoryId = searchParams.get('category_id') ?? undefined

  const [categories, setCategories] = useState<Category[]>([])
  const [question, setQuestion] = useState<QuestionForPractice | null>(null)
  const [selected, setSelected] = useState<number | null>(null)
  const [result, setResult] = useState<AnswerResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [empty, setEmpty] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchQuestion = useCallback(async () => {
    if (!certType) return
    setLoading(true)
    setQuestion(null)
    setSelected(null)
    setResult(null)
    setEmpty(false)
    setError(null)
    try {
      const q = await getNextQuestion({
        certification_type: certType as CertificationType,
        category_id: categoryId,
        mode: 'practice',
      })
      if (!q) setEmpty(true)
      else setQuestion(q)
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError('問題の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [certType, categoryId])

  useEffect(() => {
    if (!certType) return
    listCategories(certType as CertificationType).then((res) => setCategories(res?.categories ?? []))
    fetchQuestion()
  }, [certType, fetchQuestion])

  const handleSubmit = async () => {
    if (!question?.id || selected == null) return
    setSubmitting(true)
    setError(null)
    try {
      const r = await submitAnswer(question.id, {
        user_answer: selected,
        answered_at: new Date().toISOString(),
      })
      setResult(r)
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError('解答の送信に失敗しました')
    } finally {
      setSubmitting(false)
    }
  }

  const catName = categories.find((c) => c.id === categoryId)?.name_ja ?? ''

  if (!certType) return null

  // 空状態 (EF-03)
  if (empty) {
    return (
      <div className="mx-auto max-w-content p-screen">
        <h1 className="mb-6 text-[22px] font-bold tracking-tight">問題演習</h1>
        <div data-testid="practice-empty" className="rounded-lg border border-border bg-surface px-6 py-12 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-surface-alt text-text-muted">
            <Icon name="pencil" size={26} />
          </div>
          <div className="text-base font-semibold text-text-primary">この分野の問題は準備中です</div>
          <div className="mt-1 text-sm text-text-muted">しばらくお待ちください</div>
          <button onClick={() => navigate('/')} className="btn-primary mt-5 px-5">
            ホームへ戻る
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-content p-screen">
      {/* ヘッダー */}
      <div data-testid="practice-setup" className="mb-5 flex items-center justify-between">
        <h1 className="text-xl font-bold tracking-tight">問題演習</h1>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[13px] text-text-secondary transition-colors hover:text-text-primary"
        >
          <Icon name="x-circle" size={16} /> 中断
        </button>
      </div>

      {error && (
        <div role="alert" className="alert-error mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <PageLoader />
      ) : question ? (
        <>
          {/* カテゴリラベル */}
          <div className="mb-1.5 text-xs text-text-muted">{catName || question.category_name_ja}</div>

          {/* 問題文 */}
          <div data-testid="question-text" className="mb-5 text-base font-medium leading-body text-text-primary">
            {question.question_text}
          </div>

          {/* 選択肢 */}
          <div data-testid="choice-list" className="mb-5 flex flex-col gap-2">
            {question.choices?.map((c) => (
              <ChoiceOption
                key={c.choice_no}
                choiceNo={c.choice_no ?? 0}
                choiceText={c.choice_text ?? ''}
                selected={selected === c.choice_no}
                onSelect={() => !result && setSelected(c.choice_no ?? null)}
                correctChoice={result?.correct_choice}
                userAnswer={result?.user_answer}
                disabled={!!result || submitting}
              />
            ))}
          </div>

          {/* 解答結果 */}
          {result && (
            <>
              <div
                data-testid="result-banner"
                className={[
                  'mb-4 flex items-center gap-2 rounded-md border px-3.5 py-3 text-base font-bold',
                  result.is_correct
                    ? 'border-correct bg-correct-bg text-correct-on'
                    : 'border-incorrect bg-incorrect-bg text-incorrect-on',
                ].join(' ')}
              >
                <Icon name={result.is_correct ? 'check' : 'x'} size={18} aria-hidden={false} />
                {result.is_correct ? '正解！' : '不正解'}
                {result.added_to_review && (
                  <span className="text-xs font-normal">復習リストに追加されました</span>
                )}
              </div>

              <div data-testid="explanation" className="card mb-1 px-4 py-3.5">
                <div className="mb-2 text-[13px] font-bold text-text-secondary">解説</div>
                <div className="text-[15px] leading-body">{result.explanation}</div>
              </div>

              <SourceBlock
                sourceLaw={result.source_law}
                sourceReference={result.source_reference}
                confirmedDate={result.confirmed_date}
              />
            </>
          )}

          {/* ボタン */}
          {!result ? (
            <button
              data-testid="answer-submit"
              onClick={handleSubmit}
              disabled={selected == null || submitting}
              className="btn-primary mt-2 w-full"
            >
              {submitting ? <Spinner label="送信中..." /> : '解答する'}
            </button>
          ) : (
            <button data-testid="next-question" onClick={fetchQuestion} className="btn-primary mt-4 w-full">
              次の問題へ
            </button>
          )}
        </>
      ) : null}
    </div>
  )
}
